import axios from 'axios';
import keycloak from './keycloak';
import { KEYCLOAK_CONFIG } from '../config/apiConfig';

// Helper to safely decode JWT
const decodeToken = (token) => {
    try {
        if (!token) return null;
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error('Error decoding token:', e);
        return null;
    }
};

// Helper to save current tokens to localStorage
const saveTokens = () => {
    if (keycloak.token) {
        localStorage.setItem('kc_token', keycloak.token);
        localStorage.setItem('kc_refreshToken', keycloak.refreshToken || '');
        localStorage.setItem('kc_idToken', keycloak.idToken || '');
    }
};

// Helper to restore keycloak instance from localStorage
const restoreFromLocalStorage = () => {
    const token = localStorage.getItem('kc_token');
    const refreshToken = localStorage.getItem('kc_refreshToken');
    const idToken = localStorage.getItem('kc_idToken');

    if (token) {
        // Validate if stored refresh token has expired before attempting restoration
        if (refreshToken) {
            const refreshTokenParsed = decodeToken(refreshToken);
            if (refreshTokenParsed && refreshTokenParsed.exp) {
                const now = Math.floor(Date.now() / 1000);
                if (refreshTokenParsed.exp < now) {
                    console.warn('[AuthService] Stored refresh token is expired - clearing session');
                    localStorage.removeItem('kc_token');
                    localStorage.removeItem('kc_refreshToken');
                    localStorage.removeItem('kc_idToken');
                    keycloak.authenticated = false;
                    return false;
                }
            }
        }

        keycloak.token = token;
        keycloak.refreshToken = refreshToken || undefined;
        keycloak.idToken = idToken || undefined;
        keycloak.authenticated = true;

        // Decode and set parsed tokens
        keycloak.tokenParsed = decodeToken(token);
        if (refreshToken) {
            keycloak.refreshTokenParsed = decodeToken(refreshToken);
        }
        if (idToken) {
            keycloak.idTokenParsed = decodeToken(idToken);
        }

        // Calculate time skew
        if (keycloak.tokenParsed && keycloak.tokenParsed.iat) {
            const now = Math.floor(Date.now() / 1000);
            keycloak.timeSkew = now - keycloak.tokenParsed.iat;
        }

        return true;
    }

    return false;
};

// Register keycloak callbacks to keep localStorage in sync
keycloak.onAuthRefreshSuccess = () => {
    saveTokens();
};

keycloak.onAuthRefreshError = () => {
    console.error('Failed to refresh token');
    // Clear tokens on hard refresh error
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_refreshToken');
    localStorage.removeItem('kc_idToken');
};

export const authService = {
    login: async (username, password) => {
        // If username/password are provided, use Direct Grant (ROPC)
        if (username && password) {
            const params = new URLSearchParams();
            params.append('grant_type', 'password');
            params.append('client_id', KEYCLOAK_CONFIG.clientId);
            params.append('username', username);
            params.append('password', password);
            params.append('scope', 'openid profile email roles');

            // Use relative URL to leverage Vite proxy and avoid CORS
            // The proxy in vite.config.js will forward /realms/* to the backend
            const realm = KEYCLOAK_CONFIG.realm;
            const tokenUrl = `/realms/${realm}/protocol/openid-connect/token`;

            try {
                const response = await axios.post(tokenUrl, params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });


                const { access_token, refresh_token, id_token } = response.data;

                // Store in localStorage for persistence
                localStorage.setItem('kc_token', access_token);
                if (refresh_token) {
                    localStorage.setItem('kc_refreshToken', refresh_token);
                }
                if (id_token) {
                    localStorage.setItem('kc_idToken', id_token);
                }

                // Manually set tokens in keycloak-js instance
                keycloak.token = access_token;
                keycloak.refreshToken = refresh_token;
                keycloak.idToken = id_token;

                keycloak.tokenParsed = decodeToken(access_token);
                if (refresh_token) {
                    keycloak.refreshTokenParsed = decodeToken(refresh_token);
                }
                if (id_token) {
                    keycloak.idTokenParsed = decodeToken(id_token);
                }

                // Calculate time skew to prevent immediate subsequent refreshes
                // This is crucial for keycloak-js to know if the token is actually expired
                if (keycloak.tokenParsed && keycloak.tokenParsed.iat) {
                    const now = Math.floor(Date.now() / 1000);
                    keycloak.timeSkew = now - keycloak.tokenParsed.iat;
                }

                keycloak.authenticated = true;

                // Persist initially
                saveTokens();

                return true;
            } catch (error) {
                console.error('Direct login failed', error);
                if (error.response) {
                    console.error('Response status:', error.response.status);
                    console.error('Response data:', JSON.stringify(error.response.data));
                }
                throw new Error('Invalid credentials');
            }
        }
        // Never use standard redirect (we want to keep them on the React UI)
        throw new Error('Direct login requires username and password');
    },

    logout: async () => {
        try {
            // Clear localStorage
            localStorage.removeItem('kc_token');
            localStorage.removeItem('kc_refreshToken');
            localStorage.removeItem('kc_idToken');

            // Fallback: Clear local state and force redirect locally
            keycloak.authenticated = false;
            keycloak.token = null;
            keycloak.refreshToken = null;
            keycloak.idToken = null;
            
            window.location.href = '/login';
        } catch (error) {
            console.error('Logout failed:', error);
            window.location.href = '/login';
        }
    },

    getCurrentUser: () => {
        // If not authenticated in-memory, try to restore from localStorage
        if (!keycloak.authenticated) {
            restoreFromLocalStorage();
        }

        if (keycloak.authenticated && keycloak.tokenParsed) {
            return {
                id: keycloak.tokenParsed.sub,
                username: keycloak.tokenParsed.preferred_username,
                name: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username,
                email: keycloak.tokenParsed.email,
                roles: keycloak.tokenParsed.realm_access?.roles || [],
                supplierPublicId: keycloak.tokenParsed.supplier_public_id || keycloak.tokenParsed.tenant_id,
                primaryRole: keycloak.tokenParsed.realm_access?.roles?.includes('SALES_PERSON') ? 'SALES_PERSON' : 'ADMIN',
                realmRoles: keycloak.tokenParsed.realm_access?.roles || []
            };
        }
        return null;
    },

    getToken: () => {
        // First try to get from keycloak instance
        if (keycloak.token) {
            return keycloak.token;
        }

        // Fallback to localStorage if keycloak instance doesn't have it
        const storedToken = localStorage.getItem('kc_token');
        if (storedToken) {
            // Restore it to keycloak instance for future use
            keycloak.token = storedToken;
            return storedToken;
        }

        console.warn('No token available in keycloak instance or localStorage');
        return null;
    },

    refreshPromise: null,

    updateToken: async (minValidity = 5) => {
        // Check if there's already a refresh in progress
        if (authService.refreshPromise) {
            return authService.refreshPromise;
        }

        // Check if we have a refresh token
        let refreshToken = keycloak.refreshToken;
        if (!refreshToken) {
            refreshToken = localStorage.getItem('kc_refreshToken');
            if (!refreshToken) {
                console.warn('[AuthService] No refresh token available - session cannot be extended');
                return false;
            }
        }

        // Validate refresh token hasn't expired
        const refreshTokenParsed = keycloak.refreshTokenParsed || decodeToken(refreshToken);
        if (refreshTokenParsed && refreshTokenParsed.exp) {
            const now = Math.floor(Date.now() / 1000);
            if (refreshTokenParsed.exp < now) {
                console.error('[AuthService] Refresh token has expired - user must re-authenticate');
                // Clear expired tokens
                localStorage.removeItem('kc_token');
                localStorage.removeItem('kc_refreshToken');
                localStorage.removeItem('kc_idToken');
                keycloak.authenticated = false;
                return false;
            }
        }

        // Check if current access token needs refresh
        const currentToken = keycloak.token || localStorage.getItem('kc_token');
        if (currentToken) {
            const tokenParsed = decodeToken(currentToken) || keycloak.tokenParsed;
            if (tokenParsed && tokenParsed.exp) {
                const now = Math.floor(Date.now() / 1000);
                const timeLeft = tokenParsed.exp - now;

                // If token is still valid for more than minValidity seconds, no refresh needed
                if (timeLeft > minValidity) {
                    return false;
                }
            } else {
                // Static or non-expiring mock token — no Keycloak refresh required
                return false;
            }
        }

        authService.refreshPromise = (async () => {
            try {

                // Manual refresh using Keycloak token endpoint
                const params = new URLSearchParams();
                params.append('grant_type', 'refresh_token');
                params.append('client_id', KEYCLOAK_CONFIG.clientId);
                params.append('refresh_token', refreshToken);

                const realm = KEYCLOAK_CONFIG.realm;
                const tokenUrl = `/realms/${realm}/protocol/openid-connect/token`;

                const response = await axios.post(tokenUrl, params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });

                const { access_token, refresh_token: new_refresh_token, id_token } = response.data;


                // Update keycloak instance
                keycloak.token = access_token;
                keycloak.refreshToken = new_refresh_token || refreshToken; // Use new refresh token if provided
                keycloak.idToken = id_token;
                keycloak.authenticated = true;

                // Parse tokens
                keycloak.tokenParsed = decodeToken(access_token);
                if (new_refresh_token) {
                    keycloak.refreshTokenParsed = decodeToken(new_refresh_token);
                }
                if (id_token) {
                    keycloak.idTokenParsed = decodeToken(id_token);
                }

                // Recalculate time skew
                if (keycloak.tokenParsed && keycloak.tokenParsed.iat) {
                    const now = Math.floor(Date.now() / 1000);
                    keycloak.timeSkew = now - keycloak.tokenParsed.iat;
                }

                // Save to localStorage
                saveTokens();

                return true; // Token was refreshed
            } catch (error) {
                console.error('[AuthService] Failed to refresh token:', error);

                // Check if this is a permanent failure (refresh token expired/invalid)
                if (error.response) {
                    const errorData = error.response.data;
                    if (errorData.error === 'invalid_grant' ||
                        errorData.error_description?.includes('expired') ||
                        errorData.error_description?.includes('invalid')) {
                        console.error('[AuthService] Refresh token is invalid or expired - clearing session');
                        localStorage.removeItem('kc_token');
                        localStorage.removeItem('kc_refreshToken');
                        localStorage.removeItem('kc_idToken');
                    }
                }

                return false;
            } finally {
                authService.refreshPromise = null;
            }
        })();

        return authService.refreshPromise;
    },

    isAuthenticated: () => {
        // Check keycloak instance first
        if (keycloak.authenticated) {
            return true;
        }

        // Fallback: restore from localStorage
        // This handles cases where keycloak instance loses state during navigation
        return restoreFromLocalStorage();
    }
};
